import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { confirm, notify } from '../../services/dialog';
import Avatar from '../ui/Avatar';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import CallButton from '../voice/CallButton';
import useAsync from '../../hooks/useAsync';
import {
  ASSIGNED_RIDE_STATUSES,
  cancelRide,
  fetchLiveRide,
  type Ride,
  type RideStatus,
} from '../../services/rides';
import { getAccessToken } from '../../services/client';
import { useVoiceEligibility } from '../../voice/VoiceCallProvider';
import { colors, hairline, radii, screenPadding, spacing } from '../../theme';

/**
 * The trip that is happening right now.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS LIVES ON THE BOOKINGS TAB
 * ---------------------------------------------------------------------------
 * A customer ride is a standalone `customer_trip` with no `bookingId`. It has
 * no booking, so it has no booking detail screen, so there is nowhere else in
 * the app for it to live - the professional's equivalent hangs off
 * `BookingDetailScreen`.
 *
 * The Bookings tab is the app's live-activity surface, so the card sits at the
 * top of that list for as long as the trip is going on.
 *
 * ---------------------------------------------------------------------------
 * WHY IT SHOWS BEFORE A DRIVER EXISTS
 * ---------------------------------------------------------------------------
 * It used to return null unless `riderId` was set, which meant somebody could
 * book a driver, be dropped back on this tab, and find an empty list. The
 * booking had worked; nothing said so. "Did that go through?" is the worst
 * question an app can leave anyone asking, and the fix is not a toast - it is
 * showing the trip from the moment it exists.
 *
 * So `requested` and `assigning` render too, with the searching state and no
 * call button, because there is genuinely nobody to ring yet.
 */

/** How often the ride is re-read once a driver is on it. */
const RIDE_POLL_MS = 30000;

/**
 * And how often while we are still looking for one.
 *
 * Faster because this is the only window where the customer is actively
 * waiting on an answer rather than watching a car approach - and it is short,
 * so the extra requests are bounded by dispatch rather than by the trip.
 */
const SEARCHING_POLL_MS = 8000;

/** What the driver is doing, in the customer's words. */
const STATUS_COPY: Record<RideStatus, string> = {
  requested: 'Finding you a driver',
  assigning: 'Finding you a driver',
  assigned: 'Driver assigned',
  en_route: 'Your driver is on the way',
  arrived: 'Your driver has arrived',
  started: 'On your way',
  completed: 'Trip finished',
  cancelled: 'Trip cancelled',
  unassignable: 'No driver available',
};

const isSearching = (status: RideStatus): boolean =>
  status === 'requested' || status === 'assigning';

/** "Swift Dzire · White" from whichever of those the rider actually filled in. */
const describeVehicle = (ride: Ride): string | null => {
  const vehicle = ride.vehicle;
  if (!vehicle) return null;
  const model = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
  return (
    [model || vehicle.vehicleType, vehicle.colour]
      .filter(Boolean)
      .join(' · ')
      .trim() || null
  );
};

function DriverCard({
  /**
   * Opens the safety screen for this trip.
   *
   * Passed in rather than navigated to from here, matching `SosButton` on a
   * booking: the alarm lives on a screen of its own where nothing else can be
   * pressed by accident, and whoever mounted this card owns that navigation.
   *
   * Optional so a caller that has nowhere to send it renders the card without
   * the row - a dead button on a safety control is worse than no button.
   */
  onSos,
}: {
  onSos?: (ride: {
    id: string;
    driverName?: string | null;
    plate?: string | null;
  }) => void;
} = {}) {
  /* Not reactive, and does not need to be: signing in re-renders this tree
     from `HomeScreen`, which is the moment the answer changes. */
  const signedIn = !!getAccessToken();
  const [cancelling, setCancelling] = useState(false);

  const ride = useAsync<Ride | null>(() => fetchLiveRide(), [], {
    enabled: signedIn,
  });

  /*
   * Through a ref, and that detail is load-bearing: `useAsync` builds a fresh
   * `reload` arrow on every render, so an effect depending on it directly
   * would tear the interval down and rebuild it each draw - an interval
   * replaced every render never reaches its delay, so it would never fire.
   */
  const reload = useRef(ride.reload);
  reload.current = ride.reload;

  const live = ride.data;
  const searching = live ? isSearching(live.status) : false;

  useEffect(() => {
    if (!signedIn) return;
    const every = searching ? SEARCHING_POLL_MS : RIDE_POLL_MS;
    const id = setInterval(() => reload.current(), every);
    return () => clearInterval(id);
  }, [signedIn, searching]);

  /*
   * Asked here rather than left to `CallButton`, because this card wants the
   * name as well as the permission and one request should answer both. Two
   * could land out of order and name a different person from the one the
   * button would ring.
   */
  const eligibility = useVoiceEligibility('ride', live?.id);

  if (!signedIn || !live) return null;

  const assigned = ASSIGNED_RIDE_STATUSES.includes(live.status);

  /*
   * Eligibility first: it returns the name the OTHER handset shows when this
   * call lands, so the two screens cannot name the same person differently.
   * The vehicle snapshot is the fallback - frozen onto the ride at assignment,
   * so it is present whenever a driver is, and it means a slow eligibility
   * request no longer blanks the whole card.
   */
  const name =
    eligibility.data?.counterpart?.displayName?.trim() ||
    live.vehicle?.riderName?.trim() ||
    null;

  const vehicle = describeVehicle(live);
  const plate = live.vehicle?.registrationNumber?.trim() || null;

  const confirmCancel = async () => {
    const cancel = await confirm({
      title: 'Cancel this trip?',
      message: searching
        ? 'We will stop looking for a driver.'
        : 'Your driver has already been assigned.',
      confirmLabel: 'Cancel trip',
      cancelLabel: 'Keep it',
    });
    if (!cancel) return;

    /*
     * Flat, rather than the promise chain this replaces. The old shape had to
     * nest the whole cancellation inside an `onPress`, which is what pushed
     * the failure handler three levels in and left `setCancelling(false)` in a
     * `finally` a long way from the `true` that paired with it.
     */
    setCancelling(true);
    try {
      await cancelRide(live.id, 'Cancelled from the app');
      reload.current();
    } catch (error) {
      console.error('[ride] cancel failed', error);
      notify(
        'Could not cancel',
        error instanceof Error
          ? error.message
          : 'Please try again in a moment.',
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <View style={styles.card}>
      {/* --- Who, and what they are doing ---------------------------------- */}
      <View style={styles.row}>
        {assigned && name ? (
          <Avatar name={name} size={44} tone="muted" />
        ) : (
          <View style={styles.searchingAvatar}>
            <ActivityIndicator size="small" color={colors.primary} />
          </View>
        )}

        <View style={styles.text}>
          <View style={styles.heading}>
            <Icon name="target" size={14} color={colors.primary} />
            <Text variant="labelSm" color={colors.primary}>
              {STATUS_COPY[live.status]}
            </Text>
          </View>

          <Text variant="subtitle" numberOfLines={1}>
            {assigned ? name ?? 'Your driver' : 'Looking for a driver nearby'}
          </Text>

          {vehicle || plate ? (
            <Text variant="caption" color={colors.inkMuted} numberOfLines={1}>
              {[vehicle, plate].filter(Boolean).join('  •  ')}
            </Text>
          ) : null}
        </View>
      </View>

      {/* --- The OTP, only while they are waiting at the kerb -------------- */}
      {live.status === 'arrived' && live.startOtpCode ? (
        <View style={styles.otp}>
          <Text variant="caption" color={colors.inkMuted}>
            Show this to your driver
          </Text>
          <Text variant="titleLg" color={colors.ink} style={styles.otpCode}>
            {live.startOtpCode}
          </Text>
        </View>
      ) : null}

      {/* --- Where ---------------------------------------------------------- */}
      {live.pickupAddressText || live.dropAddressText ? (
        <View style={styles.legs}>
          {live.pickupAddressText ? (
            <View style={styles.leg}>
              <View style={[styles.dot, styles.dotPickup]} />
              <Text variant="caption" numberOfLines={1} style={styles.legText}>
                {live.pickupAddressText}
              </Text>
            </View>
          ) : null}
          {live.dropAddressText ? (
            <View style={styles.leg}>
              <View style={[styles.dot, styles.dotDrop]} />
              <Text variant="caption" numberOfLines={1} style={styles.legText}>
                {live.dropAddressText}
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}

      {/* --- Estimate ------------------------------------------------------- */}
      {live.estimatedMinutes != null || live.estimatedDistanceKm != null ? (
        <View style={styles.meta}>
          {live.estimatedMinutes != null ? (
            <View style={styles.metaItem}>
              <Icon name="clock" size={13} color={colors.inkMuted} />
              <Text variant="caption" color={colors.inkMuted}>
                {`About ${live.estimatedMinutes} min`}
              </Text>
            </View>
          ) : null}
          {live.estimatedDistanceKm != null ? (
            <View style={styles.metaItem}>
              <Icon name="pin" size={13} color={colors.inkMuted} />
              <Text variant="caption" color={colors.inkMuted}>
                {`${live.estimatedDistanceKm.toFixed(1)} km`}
              </Text>
            </View>
          ) : null}
          {live.rideNumber ? (
            <Text variant="caption" color={colors.inkMuted}>
              {live.rideNumber}
            </Text>
          ) : null}
        </View>
      ) : null}

      {/* --- Actions -------------------------------------------------------- */}
      {assigned ? (
        /* No `label`: the button names the driver itself once eligibility has
           told it who they are, and falls back to "Call in app" until then. */
        <CallButton
          subjectType="ride"
          subjectId={live.id}
          eligibility={eligibility.data}
          loading={eligibility.loading}
          onStaleCallCleared={eligibility.reload}
          style={styles.call}
        />
      ) : null}

      {/*
        --------------------------------------------------------------------
        THE PANIC BUTTON, AND WHY IT IS ON THIS CARD
        --------------------------------------------------------------------
        A customer ride is a standalone `customer_trip` with no booking, so it
        has no detail screen - this card IS the trip's surface in the app, and
        without a way in from here somebody sitting in a car with a driver they
        have never met had no panic path at all. The job side has had one on
        `BookingDetailScreen` since safety shipped.

        Shown from the moment a driver is assigned rather than only while the
        trip is under way: being collected by the wrong car is exactly the
        situation this exists for, and `arrived` is when it happens.

        Quiet, like `SosButton` - outlined rather than a red slab. This sits on
        a trip that is going perfectly well, which is nearly all of them, and it
        has to be findable in two seconds without making the app feel dangerous
        for the other fifty-nine minutes.
      */}
      {assigned && onSos ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Emergency help. Opens the safety screen for this trip."
          accessibilityHint="You will be asked to press and hold before an alert is sent"
          onPress={() => onSos({ id: live.id, driverName: name, plate })}
          hitSlop={8}
          style={({ pressed }) => [
            styles.sos,
            pressed ? styles.sosPressed : null,
          ]}
        >
          <Icon name="siren" size={16} color={colors.danger} />
          <Text variant="labelSm" color={colors.danger}>
            Emergency help
          </Text>
        </Pressable>
      ) : null}

      {/* Cancellable right up to the moment the trip starts; after that only
          ops can stop it, because somebody is in the car. */}
      {live.status !== 'started' ? (
        <Pressable
          onPress={() => {
            confirmCancel();
          }}
          disabled={cancelling}
          hitSlop={8}
          style={styles.cancel}
        >
          <Text variant="caption" color={colors.danger}>
            {cancelling ? 'Cancelling…' : 'Cancel trip'}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  sos: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    ...hairline,
    borderColor: colors.danger,
    backgroundColor: colors.dangerSurface,
  },
  sosPressed: {
    opacity: 0.7,
  },
  card: {
    marginHorizontal: screenPadding,
    marginBottom: spacing.md,
    padding: spacing.lg,
    gap: spacing.md,
    borderRadius: radii.lg,
    borderWidth: hairline.borderWidth,
    borderColor: hairline.borderColor,
    backgroundColor: colors.surface,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  searchingAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  text: { flex: 1, gap: 2 },
  heading: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  otp: {
    alignItems: 'center',
    gap: 2,
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: colors.background,
  },
  otpCode: { fontWeight: '700', letterSpacing: 4 },
  legs: { gap: spacing.xs },
  leg: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  legText: { flex: 1 },
  dot: { width: 8, height: 8, borderRadius: 4 },
  dotPickup: { backgroundColor: colors.primary },
  dotDrop: { backgroundColor: colors.danger },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  metaItem: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  call: { alignSelf: 'stretch' },
  cancel: { alignSelf: 'center', paddingVertical: spacing.xs },
});

export default DriverCard;
